import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import api from '@/lib/api/axios'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { useCan } from '@/hooks/useCan'

/**
 * A business service's recovery targets: the time it is meant to be back in, and the longest
 * the business can be without it (13 Oct 2026). The customer's own figures - nothing works them
 * out - and what the impact assessment draws the service's recovery time against.
 *
 * Saved on its own, not with the rest of the form: the two figures live in their own table
 * (backend/src/lib/service-targets.js), so the object form's save is untouched. Entered in
 * hours, stored in minutes; an empty box means "not set".
 */
interface Targets { rtoMinutes: number | null; mtpdMinutes: number | null; updatedByName: string | null; updatedAt: string | null }
const hours = (minutes: number | null) => (minutes == null ? '' : String(Math.round((minutes / 60) * 100) / 100))
const minutes = (text: string): number | null | undefined => {
    if (text.trim() === '') return null
    const n = Number(text)
    return Number.isFinite(n) && n > 0 ? Math.round(n * 60) : undefined
}

export function ServiceRecoveryTargets({ objectId }: { objectId: string }) {
    const { t } = useTranslation()
    const queryClient = useQueryClient()
    const canEdit = useCan('objects.update')
    const key = ['objects', objectId, 'recovery-targets']
    const { data } = useQuery({ queryKey: key, queryFn: async () => (await api.get<{ data: Targets }>(`/api/objects/${encodeURIComponent(objectId)}/recovery-targets`)).data.data })
    const [rto, setRto] = useState('')
    const [mtpd, setMtpd] = useState('')
    const [problem, setProblem] = useState<string | null>(null)
    useEffect(() => { if (data) { setRto(hours(data.rtoMinutes)); setMtpd(hours(data.mtpdMinutes)) } }, [data])

    const save = useMutation({
        mutationFn: async (body: { rtoMinutes: number | null; mtpdMinutes: number | null }) => (await api.put<{ data: Targets }>(`/api/objects/${encodeURIComponent(objectId)}/recovery-targets`, body)).data.data,
        onSuccess: (saved) => { queryClient.setQueryData(key, saved); queryClient.invalidateQueries({ queryKey: ['recovery-sets'] }) },
        onError: (error) => setProblem((error as { response?: { data?: { message?: string } } })?.response?.data?.message ?? t('objects.recoveryTargets.saveFailed', 'The targets could not be saved.')),
    })
    const submit = () => {
        const rtoMinutes = minutes(rto)
        const mtpdMinutes = minutes(mtpd)
        if (rtoMinutes === undefined || mtpdMinutes === undefined) return setProblem(t('objects.recoveryTargets.invalid', 'Enter a number of hours greater than zero, or leave the box empty.'))
        if (rtoMinutes != null && mtpdMinutes != null && mtpdMinutes < rtoMinutes) return setProblem(t('objects.recoveryTargets.order', 'The maximum tolerable outage cannot be shorter than the recovery time target.'))
        setProblem(null)
        save.mutate({ rtoMinutes, mtpdMinutes })
    }
    const changed = !!data && (rto !== hours(data.rtoMinutes) || mtpd !== hours(data.mtpdMinutes))

    return (
        <div className="col-span-full rounded-lg border p-3" data-testid="service-recovery-targets">
            <h3 className="text-sm font-semibold">{t('objects.recoveryTargets.title', 'Recovery targets')}</h3>
            <p className="mt-0.5 text-xs text-muted-foreground">{t('objects.recoveryTargets.help', 'Your own figures for this business service. The impact assessment shows its recovery time against them. Leave a box empty if it is not set.')}</p>
            <div className="mt-2 flex flex-wrap items-end gap-3">
                <label className="text-xs font-medium">
                    {t('objects.recoveryTargets.rto', 'Recovery time target (hours)')}
                    <Input className="mt-1 h-9 w-40" type="number" min="0" step="0.5" inputMode="decimal" value={rto} disabled={!canEdit || save.isPending} onChange={(e) => setRto(e.target.value)} />
                </label>
                <label className="text-xs font-medium">
                    {t('objects.recoveryTargets.mtpd', 'Maximum tolerable outage (hours)')}
                    <Input className="mt-1 h-9 w-40" type="number" min="0" step="0.5" inputMode="decimal" value={mtpd} disabled={!canEdit || save.isPending} onChange={(e) => setMtpd(e.target.value)} />
                </label>
                {canEdit && <Button type="button" size="sm" variant="outline" onClick={submit} disabled={!changed || save.isPending}>{t('objects.recoveryTargets.save', 'Save targets')}</Button>}
                {save.isSuccess && !changed && <span className="text-xs text-muted-foreground" role="status">{t('objects.recoveryTargets.saved', 'Saved')}</span>}
            </div>
            {problem && <p role="alert" className="mt-2 text-xs font-medium text-destructive">{problem}</p>}
        </div>
    )
}
